import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useForm from '@afosto/utils-react/validation/useForm';
import useRegisterInput from '@afosto/app-components-react/Form/useRegisterInput';
import useScopes from '../../utils/useScopes';
import ScopesAutocomplete from '../ScopesAutocomplete';
import validationSchema, { editValidationSchema } from './validations';
import translations from './translations';
import useIntl from '@afosto/utils-react/intl/useIntl';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import Icon from '@afosto/app-components-react/Icon';
import { grantTypes as availableGrantTypes } from '../../data/formOptions';
import Autocomplete from '@afosto/app-components-react/Autocomplete';
import TextField from '@afosto/app-components-react/TextField';
import Form from '@afosto/app-components-react/Form';
import ScopesTree from '../ScopesTree';

const OauthClientForm = ({ oauthClient, inline, onSubmit, onCancel }) => {
  const intl = useIntl();
  const [openTooltipID, setOpenTooltipID] = useState(null);
  const [availableScopes, isFetchingScopes] = useScopes();
  const {
    id = '',
    name = '',
    client_id: clientId = '',
    client_secret: clientSecret = '',
    redirect_uri: redirectUri = '',
    grant_types: grantTypes = [],
    scope: scopes = [],
  } = oauthClient || {};

  const formattedScopes = scopes.map(scopeID => ({
    label: scopeID,
    description:
      (availableScopes &&
        availableScopes.some(availableScope => availableScope.label === scopeID) &&
        availableScopes.find(availableScope => availableScope.label === scopeID).description) ||
      '',
  }));

  const formattedGrantTypes = grantTypes.map(grantType => ({
    value: grantType,
    label:
      (availableGrantTypes &&
        availableGrantTypes.some(availableGrantType => availableGrantType.value === grantType) &&
        availableGrantTypes.find(availableGrantType => availableGrantType.value === grantType)
          .label) ||
      '',
  }));

  const form = useForm({
    validationSchema: oauthClient ? editValidationSchema : validationSchema,
    editInline: inline,
    defaultValues: {
      id,
      name,
      clientId,
      clientSecret,
      redirectUri,
      grantTypes: formattedGrantTypes,
      scopes: formattedScopes,
    },
    onCancel,
    onSubmit: async data => onSubmit({ ...data, id }),
  });
  const { setIsEditing, setValue, watch } = form;
  useRegisterInput('scopes', true, form);
  const scopesValue = watch('scopes');
  const clientIdValue = watch('clientId');
  const clientSecretValue = watch('clientSecret');
  const grantTypesValue = watch('grantTypes');
  const isImplicit = grantTypesValue.includes('implicit') && grantTypesValue.length === 1;

  useEffect(() => {
    if (inline) {
      setIsEditing(false);
    }
  }, [oauthClient, inline, setIsEditing]);

  useEffect(() => {
    if (isImplicit) {
      setValue('scopes', []);
    }
  }, [isImplicit, setValue]);

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form}>
        <TextField
          name="name"
          label={intl.formatMessage(translations.fields.oauthClientName.label)}
          readOnly={form.editInline}
        />

        {(form.editInline || !oauthClient) && (
          <>
            <TextField
              name="redirectUri"
              label={intl.formatMessage(translations.fields.redirectUri.label)}
              readOnly={form.editInline}
              type="url"
            />

            <Autocomplete
              name="grantTypes"
              label={intl.formatMessage(translations.fields.grantTypes.label)}
              options={availableGrantTypes}
              readOnly={form.editInline}
              multiple
            />

            {form.editInline && oauthClient && (
              <ScopesAutocomplete
                name="scopes"
                label={intl.formatMessage(translations.fields.scopes.label)}
                options={availableScopes}
                loading={isFetchingScopes}
                readOnly={form.editInline}
                disabled={isImplicit}
              />
            )}
          </>
        )}
        {!form.editInline && (
          <ScopesTree
            label={intl.formatMessage(translations.fields.scopes.label)}
            onChange={values =>
              setValue(
                'scopes',
                values.reduce((acc, scope) => [...acc, { label: scope, value: scope }], []),
              )
            }
            value={scopesValue}
          />
        )}

        {form.editInline && (
          <>
            <TextField
              name="clientId"
              label={intl.formatMessage(translations.fields.oauthClientID.label)}
              readOnly
              multiline
              InputProps={{
                endAdornment:
                  clientIdValue && navigator.clipboard && navigator.clipboard.writeText ? (
                    <InputAdornment position="end">
                      <ClickAwayListener onClickAway={() => setOpenTooltipID(null)}>
                        <div>
                          <Tooltip
                            PopperProps={{
                              disablePortal: true,
                            }}
                            onClose={() => setOpenTooltipID(null)}
                            open={openTooltipID === 'clientId'}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            title={intl.formatMessage(translations.fields.oauthClientID.tooltip)}
                          >
                            <IconButton
                              edge="end"
                              aria-label={intl.formatMessage(
                                translations.fields.oauthClientID.button,
                              )}
                              onClick={() => {
                                navigator.clipboard.writeText(clientIdValue);
                                setOpenTooltipID('clientId');
                              }}
                            >
                              <Icon icon={['fal', 'copy']} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </ClickAwayListener>
                    </InputAdornment>
                  ) : null,
              }}
            />

            <TextField
              name="clientSecret"
              label={intl.formatMessage(translations.fields.oauthClientSecret.label)}
              readOnly
              multiline
              InputProps={{
                endAdornment:
                  clientSecretValue && navigator.clipboard && navigator.clipboard.writeText ? (
                    <InputAdornment position="end">
                      <ClickAwayListener onClickAway={() => setOpenTooltipID(null)}>
                        <div>
                          <Tooltip
                            PopperProps={{
                              disablePortal: true,
                            }}
                            onClose={() => setOpenTooltipID(null)}
                            open={openTooltipID === 'clientSecret'}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            title={intl.formatMessage(
                              translations.fields.oauthClientSecret.tooltip,
                            )}
                          >
                            <IconButton
                              edge="end"
                              aria-label={intl.formatMessage(
                                translations.fields.oauthClientSecret.button,
                              )}
                              onClick={() => {
                                navigator.clipboard.writeText(clientSecretValue);
                                setOpenTooltipID('clientSecret');
                              }}
                            >
                              <Icon icon={['fal', 'copy']} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </ClickAwayListener>
                    </InputAdornment>
                  ) : null,
              }}
            />
          </>
        )}
      </Form>
    </LoadingWrapper>
  );
};

OauthClientForm.propTypes = {
  oauthClient: PropTypes.object,
  inline: PropTypes.bool,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

OauthClientForm.defaultProps = {
  oauthClient: null,
  inline: false,
  onSubmit: undefined,
  onCancel: undefined,
};

export default OauthClientForm;
